import moment from 'moment';
import {
  getEndPeriod,
  getStartPeriod,
  getTotalTime,
} from '../../../../../../Calendar/utils/mainPageHelpers';
import { getWorkersAndCalendarData } from '../../../../../../Calendar/utils/getWorkersAndCalendarData';
import { views } from '../../../../../../Calendar/constants/views';
import { getSchedulePeriod } from '../../../model/workSchedulePeriod';

const getCalendarSchedulePeriod = (schedule) => {
  const period = getSchedulePeriod(schedule);

  if (!period) {
    return null;
  }

  return {
    startPeriod: period.startMonth.clone().startOf('month'),
    endPeriod: period.endMonth.clone().endOf('month').endOf('day'),
  };
};

const generateWorkerName = (index, departmentName) => {
  const surnames = [
    'Иванов',
    'Петров',
    'Сидоров',
    'Смирнов',
    'Кузнецов',
    'Попов',
    'Соколов',
    'Лебедев',
    'Козлов',
    'Новиков',
    'Морозов',
    'Петухов',
    'Волков',
    'Соловьев',
    'Васильев',
  ];
  const firstNames = [
    'Александр',
    'Дмитрий',
    'Максим',
    'Сергей',
    'Андрей',
    'Алексей',
    'Артем',
    'Илья',
    'Кирилл',
    'Михаил',
    'Никита',
    'Матвей',
    'Роман',
    'Егор',
    'Иван',
  ];
  const patrNames = [
    'Александрович',
    'Дмитриевич',
    'Максимович',
    'Сергеевич',
    'Андреевич',
    'Алексеевич',
    'Артемович',
    'Ильич',
    'Кириллович',
    'Михайлович',
    'Никитич',
    'Матвеевич',
    'Романович',
    'Егорович',
    'Иванович',
  ];

  const surnameIndex = index % surnames.length;
  const firstNameIndex =
    Math.floor(index / surnames.length) % firstNames.length;
  const patrNameIndex =
    Math.floor(index / (surnames.length * firstNames.length)) %
    patrNames.length;

  const surName = surnames[surnameIndex];
  const firstName = firstNames[firstNameIndex];
  const patrName = patrNames[patrNameIndex];
  const fullName = `${surName} ${firstName} ${patrName}`;

  return {
    id: index + 1,
    surName,
    firstName,
    patrName,
    fullName,
    phone: `+7${9000000000 + index}`,
  };
};

const getNameParts = (fullName = '') => {
  const [surName = '', firstName = '', patrName = ''] = fullName.split(' ');

  return { surName, firstName, patrName };
};

const normalizePerformer = (performer, index, schedule) => {
  if (!performer) {
    return generateWorkerName(
      schedule.id * 100 + index,
      schedule.departmentName
    );
  }

  const { surName, firstName, patrName } = getNameParts(performer.fullName);

  return {
    ...performer,
    id: performer.id ?? `${schedule.id}-${index}`,
    surName: performer.surName ?? surName,
    firstName: performer.firstName ?? firstName,
    patrName: performer.patrName ?? patrName,
    fullName: performer.fullName,
    phone: performer.phone || '',
  };
};

const getSchedulePerformers = (schedule) => {
  if (Array.isArray(schedule.performers) && schedule.performers.length) {
    return schedule.performers.map((performer, index) =>
      normalizePerformer(performer, index, schedule)
    );
  }

  return Array.from(Array(schedule.quantityOfWorkers || 0)).map((_, index) =>
    generateWorkerName(schedule.id * 100 + index, schedule.departmentName)
  );
};

const getShiftData = (dayOffset, performerIndex) => {
  const shiftIndex = (dayOffset + performerIndex) % 4;

  if (shiftIndex > 1) {
    return null;
  }

  return shiftIndex === 0
    ? {
        type: 'day',
        fromHour: 8,
        toHour: 16,
      }
    : {
        type: 'night',
        fromHour: 0,
        toHour: 8,
      };
};

const getIntervalShiftType = (fromDate, toDate) => {
  if (toDate.isAfter(fromDate, 'day') || fromDate.hour() < 8) {
    return 'night';
  }

  return 'day';
};

const normalizeScheduleWorkIntervals = (schedule) => {
  const source =
    schedule?.workIntervals || schedule?.intervals || schedule?.workerIntervals;

  if (!source) {
    return [];
  }

  if (Array.isArray(source)) {
    return source.flatMap((item) => {
      const workerId = item.workerId ?? item.executerId ?? item.executorId;
      const intervals = Array.isArray(item.intervals)
        ? item.intervals
        : Array.isArray(item.days)
        ? item.days
        : [item];

      return intervals
        .map((interval) => ({
          workerId,
          date: interval.date || interval.day || interval.workDate,
          from: interval.from || interval.startTime || interval.timeFrom,
          to: interval.to || interval.endTime || interval.timeTo,
        }))
        .filter(
          (interval) =>
            interval.workerId !== undefined &&
            interval.workerId !== null &&
            interval.date &&
            interval.from &&
            interval.to
        );
    });
  }

  return Object.entries(source).flatMap(([workerId, intervalsByDate]) =>
    Object.entries(intervalsByDate || {})
      .map(([date, interval]) => ({
        workerId,
        date,
        from: interval.from || interval.startTime || interval.timeFrom,
        to: interval.to || interval.endTime || interval.timeTo,
      }))
      .filter((interval) => interval.from && interval.to)
  );
};

const getExplicitWorkIntervalTasks = ({ schedule, performers }) => {
  const scheduleIntervals = normalizeScheduleWorkIntervals(schedule);

  if (!scheduleIntervals.length) {
    return [];
  }

  return scheduleIntervals
    .map((interval, index) => {
      const worker = performers.find(
        (performer) => String(performer.id) === String(interval.workerId)
      );

      if (!worker) {
        return null;
      }

      const fromDate = moment(
        `${interval.date} ${interval.from}`,
        'YYYY-MM-DD HH:mm',
        true
      );
      const toDate = moment(
        `${interval.date} ${interval.to}`,
        'YYYY-MM-DD HH:mm',
        true
      );

      if (!fromDate.isValid() || !toDate.isValid()) {
        return null;
      }

      if (toDate.isSameOrBefore(fromDate)) {
        toDate.add(1, 'day');
      }

      const shiftType = getIntervalShiftType(fromDate, toDate);

      return {
        id: `schedule-${schedule.id}-${interval.workerId}-${interval.date}-${index}`,
        requestId: 20000 + index,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        worker,
        fullAddress: schedule.departmentName,
        streetName: '',
        building: '',
        flat: '',
        visible: true,
        scheduleId: schedule.id,
        scheduleName: schedule.departmentName,
        shiftType,
      };
    })
    .filter(Boolean);
};

const getSelectedSchedulePeriod = (schedules) => {
  if (!Array.isArray(schedules) || schedules.length !== 1) {
    return null;
  }

  const [schedule] = schedules;
  const schedulePeriod = getCalendarSchedulePeriod(schedule);

  if (!schedulePeriod) {
    return null;
  }

  return schedulePeriod;
};

/**
 * Преобразует графики работы в задачи календаря
 * @param {Array} schedules - массив графиков
 * @param {moment} startPeriod - начальная дата периода календаря
 * @param {moment} endPeriod - конечная дата периода календаря
 * @returns {Array} массив задач в формате календаря
 */
const transformSchedulesToCalendarTasks = (
  schedules = [],
  startPeriod,
  endPeriod
) => {
  const tasks = [];

  schedules.forEach((schedule) => {
    const schedulePeriod = getCalendarSchedulePeriod(schedule);

    if (!schedulePeriod) {
      return;
    }

    const { startPeriod: scheduleStart, endPeriod: scheduleEnd } =
      schedulePeriod;

    // Определяем период пересечения графика с периодом календаря
    const periodStart = startPeriod
      ? moment.max(scheduleStart, startPeriod)
      : scheduleStart;
    const periodEnd = endPeriod
      ? moment.min(scheduleEnd, endPeriod)
      : scheduleEnd;

    if (periodStart.isAfter(periodEnd)) {
      return; // Нет пересечения
    }

    const performers = getSchedulePerformers(schedule);
    const explicitIntervalTasks = getExplicitWorkIntervalTasks({
      schedule,
      performers,
    });

    if (explicitIntervalTasks.length) {
      tasks.push(...explicitIntervalTasks);
      return;
    }

    if (schedule.isDraft) {
      return;
    }

    const scheduleStartDay = scheduleStart.clone().startOf('day');

    // Генерируем смены для каждого дня в периоде
    let currentDate = periodStart.clone().startOf('day');
    let shiftCounter = 0;

    while (currentDate.isSameOrBefore(periodEnd, 'day')) {
      const dayOffset = currentDate.diff(scheduleStartDay, 'days');

      for (
        let workerIndex = 0;
        workerIndex < performers.length;
        workerIndex += 1
      ) {
        const worker = performers[workerIndex];
        const shift = getShiftData(dayOffset, workerIndex);

        if (!shift) {
          continue;
        }

        const fromDate = currentDate
          .clone()
          .hour(shift.fromHour)
          .minute(0)
          .second(0);
        const toDate = currentDate
          .clone()
          .hour(shift.toHour)
          .minute(0)
          .second(0);

        // Создаем задачу для календаря
        const task = {
          id: `schedule-${schedule.id}-${currentDate.format(
            'YYYY-MM-DD'
          )}-${workerIndex}`,
          requestId:
            10000 + schedule.id * 100 + shiftCounter * 10 + workerIndex,
          fromDate: fromDate.toISOString(),
          toDate: toDate.toISOString(),
          worker,
          fullAddress: schedule.departmentName,
          streetName: '',
          building: '',
          flat: '',
          visible: true,
          scheduleId: schedule.id,
          scheduleName: schedule.departmentName,
          shiftType: shift.type,
          // index будет установлен позже в getWorkersAndCalendarData
        };

        tasks.push(task);
      }

      shiftCounter++;
      currentDate.add(1, 'day');
    }
  });

  return tasks;
};

/**
 * Преобразует графики в формат calendarData для Calendar
 * Использует getWorkersAndCalendarData для правильной группировки по дням
 */
export const transformSchedulesToCalendarData = (
  schedules = [],
  view,
  calendarSheet,
  mode
) => {
  const schedulePeriod =
    view === views.month ? getSelectedSchedulePeriod(schedules) : null;
  const startPeriod =
    schedulePeriod?.startPeriod ?? getStartPeriod(view, calendarSheet, mode);
  const endPeriod =
    schedulePeriod?.endPeriod ?? getEndPeriod(view, calendarSheet, mode);
  const totalTime = getTotalTime(view, startPeriod, endPeriod);

  // Calendar state might not be initialized yet (first render).
  if (!startPeriod || !endPeriod || !totalTime) {
    return [];
  }

  // Преобразуем графики в задачи
  const tasks = transformSchedulesToCalendarTasks(
    schedules,
    startPeriod,
    endPeriod
  );

  // Используем существующую функцию для группировки задач по дням
  const { calendarGridData } = getWorkersAndCalendarData({
    data: tasks,
    startPeriod,
    totalTime,
    view,
    taskId: null,
  });

  return calendarGridData;
};
